'use client';
import { Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { PickerValue } from '@mui/x-date-pickers/internals';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useUpdatePlannedFinishDate } from '@/hooks';
import { toast } from '@/utils/client/toast';

export default function PlannedFinishDate({
  courseId,
  date,
  testAt,
}: {
  courseId: string;
  date?: string;
  testAt?: string;
}) {
  const [value, setValue] = useState(dayjs(date));
  const updateDate = useUpdatePlannedFinishDate();
  const { isPending } = updateDate;
  const onChange = (newDate: PickerValue) => {
    const date = dayjs(newDate);
    setValue(date);
    updateDate.mutate(
      { courseId, date: date.toISOString() },
      {
        onSuccess: (plannedToFinishAt) => {
          setValue(dayjs(plannedToFinishAt));
          toast.success('Lernende angepasst');
        },
        onError: () => {
          toast.error('Lernende konnte nicht gespeichert werden');
        },
      },
    );
  };

  const CustomDay = (props: PickersDayProps) => {
    const { day, outsideCurrentMonth } = props;
    if (outsideCurrentMonth) {
      return <PickersDay {...props} />;
    }

    const isToday = dayjs().isSame(day, 'day');
    const isTestDay = !!testAt && dayjs(testAt).isSame(day, 'day');
    const isPlannedFinish =
      (value && dayjs(value).isSame(day, 'day')) || (date && dayjs(date).isSame(day, 'day'));

    // Build tooltip
    const labels = [];
    if (isToday) labels.push('Heute');
    if (isTestDay) labels.push('Testdatum');
    if (isPlannedFinish) labels.push('Lernende');

    // Apply styling only if needed
    const customSx = isTestDay ? { border: '3px solid', borderColor: 'error.main' } : {};

    const dayNode = <PickersDay {...props} sx={customSx} />;

    return labels.length > 0 ? (
      <Tooltip title={labels.join(' • ')}>
        <span>{dayNode}</span>
      </Tooltip>
    ) : (
      dayNode
    );
  };
  return (
    <DatePicker
      format="DD.MM.YY"
      value={value}
      minDate={dayjs()}
      maxDate={testAt ? dayjs(testAt) : dayjs().add(6, 'month')}
      onChange={onChange}
      disabled={isPending}
      slots={{ day: CustomDay }}
      sx={{
        width: 140,
        fontWeight: 'inherit',
        '& .MuiPickersSectionList-root': {
          padding: '5px 0',
        },
        '& .MuiPickersDay-root': {
          borderRadius: '50%',
          width: 36,
          height: 36,
          margin: '0 4px',
        },
        '& .MuiPickersCalendarHeader-root': {
          mb: 1,
        },
      }}
    />
  );
}
